import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Plus, Trash, PencilSimple, ListNumbers, PenNib } from "@phosphor-icons/react";

const ROLES = ["admin", "analyst", "reviewer", "auditor"];
const emptyStep = () => ({ assignee_type: "role", assignee_role: "reviewer", assignee_user_id: "", label: "", required: true });
const emptyForm = () => ({ id: null, name: "", description: "", mode: "sequential", steps: [emptyStep()] });

export default function ApprovalRoutes() {
  const [templates, setTemplates] = useState([]);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(emptyForm());
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get("/approval-route-templates").then((r) => setTemplates(r.data)).catch(() => {}), []);
  useEffect(() => { load(); api.get("/users").then((r) => setUsers(r.data)).catch(() => {}); }, [load]);

  const openNew = () => { setForm(emptyForm()); setOpen(true); };
  const openEdit = (t) => {
    setForm({
      id: t.id, name: t.name, description: t.description || "", mode: t.mode || "sequential",
      steps: (t.steps || []).map((s) => ({
        assignee_type: s.assignee_type, assignee_role: s.assignee_role || "reviewer",
        assignee_user_id: s.assignee_user_id || "", label: s.label || "", required: s.required !== false,
      })),
    });
    setOpen(true);
  };

  const setStep = (i, patch) => setForm({ ...form, steps: form.steps.map((s, idx) => (idx === i ? { ...s, ...patch } : s)) });
  const addStep = () => setForm({ ...form, steps: [...form.steps, emptyStep()] });
  const removeStep = (i) => setForm({ ...form, steps: form.steps.filter((_, idx) => idx !== i) });
  const moveStep = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= form.steps.length) return;
    const next = form.steps.slice();
    [next[i], next[j]] = [next[j], next[i]];
    setForm({ ...form, steps: next });
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Template name is required.");
    if (!form.steps.length) return toast.error("Add at least one approver step.");
    if (form.steps.some((s) => s.assignee_type === "user" && !s.assignee_user_id))
      return toast.error("Pick a person for each named-signer step.");
    const payload = {
      name: form.name.trim(), description: form.description.trim(), mode: form.mode,
      steps: form.steps.map((s) => ({
        assignee_type: s.assignee_type,
        assignee_role: s.assignee_type === "role" ? s.assignee_role : null,
        assignee_user_id: s.assignee_type === "user" ? s.assignee_user_id : null,
        label: s.label, required: s.required,
      })),
    };
    setBusy(true);
    try {
      if (form.id) await api.put(`/approval-route-templates/${form.id}`, payload);
      else await api.post("/approval-route-templates", payload);
      setOpen(false); await load(); toast.success(form.id ? "Template updated" : "Template created");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete the "${t.name}" route template?`)) return;
    try { await api.delete(`/approval-route-templates/${t.id}`); await load(); toast.success("Template deleted"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const userName = (id) => users.find((u) => u.id === id)?.name || "—";

  return (
    <div className="p-6 space-y-5" data-testid="approval-routes">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="font-display font-bold text-xl flex items-center gap-2"><ListNumbers size={20} weight="fill" />Approval Route Templates</h2>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Define a reusable, ordered signer chain once (e.g. Author → Compliance Manager → CIP Senior Manager),
            then apply it when routing documents, policy manuals, or RSAW narratives for sign-off.
          </p>
        </div>
        <Button data-testid="new-route-template-btn" onClick={openNew} className="rounded-none bg-primary hover:bg-primary/90"><Plus size={16} className="mr-1" />New Template</Button>
      </div>

      {templates.length === 0 ? (
        <div className="border border-border bg-card p-8 text-center text-sm text-muted-foreground" data-testid="no-route-templates">
          No route templates yet. Create one to standardize how documents get signed across your program.
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {templates.map((t) => (
            <div key={t.id} data-testid={`route-template-${t.id}`} className="border border-border bg-card p-4">
              <div className="flex items-start justify-between">
                <div className="min-w-0">
                  <div className="font-display font-bold text-base flex items-center gap-2"><PenNib size={16} />{t.name}</div>
                  {t.description && <p className="text-xs text-muted-foreground mt-0.5">{t.description}</p>}
                </div>
                <div className="flex gap-1 shrink-0">
                  <button data-testid={`edit-route-${t.id}`} onClick={() => openEdit(t)} className="text-muted-foreground hover:text-foreground p-1"><PencilSimple size={16} /></button>
                  <button data-testid={`delete-route-${t.id}`} onClick={() => remove(t)} className="text-muted-foreground hover:text-red-600 p-1"><Trash size={16} /></button>
                </div>
              </div>
              <div className="mt-2 flex items-center gap-2">
                <span className="text-[10px] font-semibold uppercase border border-border px-1.5 py-0.5">{t.mode}</span>
                <span className="text-xs text-muted-foreground">{(t.steps || []).length} step(s)</span>
              </div>
              <ol className="mt-3 space-y-1 text-sm list-decimal pl-5">
                {(t.steps || []).map((s, i) => (
                  <li key={`${s.label || s.assignee_role || s.assignee_user_id || "step"}-${i}`}>
                    <span className="font-semibold">{s.label || (s.assignee_type === "role" ? `${s.assignee_role} (role)` : userName(s.assignee_user_id))}</span>
                    <span className="text-xs text-muted-foreground"> — {s.assignee_type === "role" ? `any ${s.assignee_role}` : userName(s.assignee_user_id)}{s.required === false ? " · optional" : ""}</span>
                  </li>
                ))}
              </ol>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none max-w-lg max-h-[85vh] overflow-y-auto" data-testid="route-template-dialog">
          <DialogHeader><DialogTitle>{form.id ? "Edit" : "New"} Approval Route Template</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Template name</Label><Input data-testid="route-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Policy — 3-step" className="rounded-none mt-1" /></div>
            <div><Label>Description (optional)</Label><Input data-testid="route-desc-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Author → Compliance Manager → CIP Senior Manager" className="rounded-none mt-1" /></div>
            <div className="flex items-center gap-2">
              <Label>Order</Label>
              <div className="inline-flex border border-border">
                <button data-testid="route-mode-sequential" onClick={() => setForm({ ...form, mode: "sequential" })} className={`px-2 py-1 text-xs font-semibold ${form.mode === "sequential" ? "bg-primary text-primary-foreground" : "hover:bg-secondary"}`}>Sequential</button>
                <button data-testid="route-mode-parallel" onClick={() => setForm({ ...form, mode: "parallel" })} className={`px-2 py-1 text-xs font-semibold border-l border-border ${form.mode === "parallel" ? "bg-primary text-primary-foreground" : "hover:bg-secondary"}`}>Parallel</button>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Signers (in order)</Label>
              {form.steps.map((s, i) => (
                <div key={i} className="border border-border p-2 space-y-2" data-testid={`route-step-${i}`}>
                  <div className="flex items-center gap-2">
                    <div className="flex flex-col">
                      <button data-testid={`route-step-up-${i}`} onClick={() => moveStep(i, -1)} disabled={i === 0} className="text-muted-foreground hover:text-foreground disabled:opacity-30 leading-none">▲</button>
                      <button data-testid={`route-step-down-${i}`} onClick={() => moveStep(i, 1)} disabled={i === form.steps.length - 1} className="text-muted-foreground hover:text-foreground disabled:opacity-30 leading-none">▼</button>
                    </div>
                    <Select value={s.assignee_type} onValueChange={(v) => setStep(i, { assignee_type: v })}>
                      <SelectTrigger data-testid={`route-step-type-${i}`} className="rounded-none h-8 w-32 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="user">Named person</SelectItem>
                        <SelectItem value="role">By role</SelectItem>
                      </SelectContent>
                    </Select>
                    {s.assignee_type === "role" ? (
                      <Select value={s.assignee_role} onValueChange={(v) => setStep(i, { assignee_role: v })}>
                        <SelectTrigger data-testid={`route-step-role-${i}`} className="rounded-none h-8 flex-1 text-xs capitalize"><SelectValue /></SelectTrigger>
                        <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>)}</SelectContent>
                      </Select>
                    ) : (
                      <Select value={s.assignee_user_id} onValueChange={(v) => setStep(i, { assignee_user_id: v })}>
                        <SelectTrigger data-testid={`route-step-user-${i}`} className="rounded-none h-8 flex-1 text-xs"><SelectValue placeholder="Select person" /></SelectTrigger>
                        <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} ({u.role})</SelectItem>)}</SelectContent>
                      </Select>
                    )}
                    <button onClick={() => removeStep(i)} className="text-muted-foreground hover:text-red-600"><Trash size={16} /></button>
                  </div>
                  <Input data-testid={`route-step-label-${i}`} value={s.label} onChange={(e) => setStep(i, { label: e.target.value })} placeholder="Signs as… e.g. CIP Senior Manager" className="rounded-none h-8 text-xs" />
                  <label className="flex items-center gap-2 text-xs cursor-pointer">
                    <Checkbox checked={s.required} onCheckedChange={(v) => setStep(i, { required: !!v })} className="rounded-none" />
                    Required signature
                  </label>
                </div>
              ))}
              <Button data-testid="route-add-step-btn" onClick={addStep} variant="outline" size="sm" className="rounded-none"><Plus size={14} className="mr-1" />Add signer</Button>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setOpen(false)} variant="outline" className="rounded-none">Cancel</Button>
            <Button data-testid="save-route-template-btn" onClick={save} disabled={busy} className="rounded-none bg-primary hover:bg-primary/90">{form.id ? "Save changes" : "Create template"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
